import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDraftAccountCache, useDraftAccountQuery } from './api/useDraftAccountQuery';
import { FullPageLoading } from '../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { Step, StepList, StepPanel, Steps } from '../../CommonComponents/OPL/Stepper';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import {
  AccountOpeningDraftDTO,
  AccountStatus,
  CustomerType,
  FORM_WIDTH,
  stepsMap,
} from './AccountOpening.meta';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { PersonalInfo } from './steps/PersonalInfo/PersonalInfo';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { AccountInfo } from './steps/AccountInfo/AccountInfo';
import { BeneficiaryInfo } from './steps/BeneficiaryInfo/BeneficiaryInfo';
import { Review } from './steps/Review/Review';
import { AccountSelection } from './steps/AccountSelection/AccountSelection';
import { useSubmitDraftMutation } from './api/useSubmitDraftMutation';
import { Alert } from '../../CommonComponents/OPL/Alert/Alert';
import { fetchNetWorthData } from '../../ContextualSidebar/NetWorthSidebar/NetWorthSidebarActions';
import { useRouteParams } from './hooks/useRouteParams';
import { Employment } from './steps/Employment/Employment';
import { AccountOpeningComplete } from './components/AccountOpeningComplete/AccountOpeningComplete';
import { selectIsUserClient } from '../../CommonSelectors/CommonAreaSelector';
import { isFetchingSelector as selectIsFetchingNetWorth } from '../../ContextualSidebar/NetWorthSidebar/NetWorthSidebarSelector';
import { useCompleteWorkflowMutation } from './api/useCompleteWorkflowMutation';
import { MESSAGES } from '../../CommonConstants';
import { useCanAccessPersonalFinances } from './hooks/useCanAccessPersonalFinances';
import { useDocumentTitle } from '../../_hooks/useDocumentTitle';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { GliaGlobalStyle } from './AccountOpening.styles';
import { Content, StickyFooter } from '../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { useSendEmailToClientMutation } from './api/useSendEmailToClientMutation';

export const AccountOpening = () => {
  const { clientId, draftId } = useRouteParams();

  const { isLoading, isError, refetch, data } = useDraftAccountQuery({ clientId, draftId });

  useDocumentTitle('Apex Account Opening');

  useResetModuleWrapperStyles();

  if (isLoading) {
    return (
      <Box mt={96}>
        <Content>
          <FullPageLoading text="Loading..." size="medium" />
        </Content>

        <StickyFooter />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" my="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  if (data.accountOpeningStatus === AccountStatus.Draft) {
    return <AccountOpeningWorkflow data={data} />;
  }

  return <AccountOpeningComplete />;
};

interface Props {
  data: AccountOpeningDraftDTO;
}

export const AccountOpeningWorkflow = ({ data }: Props) => {
  const { data: draftAccount } = data;

  const [selectedStepIndex, setSelectedStepIndex] = useState(0);

  const { mutateDraftAccount, mutateAccountStatus } = useDraftAccountCache();

  const isUserClient = useSelector(selectIsUserClient);

  const {
    mutateAsync: submitDraft,
    isLoading: isSubmittingDraft,
    error: submitDraftError,
    reset: resetSubmitDraftMutation,
  } = useSubmitDraftMutation();

  const {
    mutateAsync: completeWorkflow,
    isLoading: isCompletingWorkflow,
    isError: isCompleteWorkflowError,
    reset: resetCompleteWorkflowMutation,
  } = useCompleteWorkflowMutation();

  const {
    mutateAsync: sendEmailToClient,
    isLoading: isSendingEmailToClient,
    isError: isSendingEmailToClientError,
    reset: resetSendingEmailToClient,
  } = useSendEmailToClientMutation();

  const { clientId, draftId, isFromWorkflow } = useRouteParams();

  const canAccessPersonalFinances = useCanAccessPersonalFinances();

  const dispatch = useDispatch();

  const isFetchingNetWorth: boolean = useSelector(selectIsFetchingNetWorth);

  const { customerType } = draftAccount;

  if (customerType === null) {
    return <AccountSelection data={data} />;
  }

  const steps = stepsMap[customerType];

  const handleSubmitDraft = async () => {
    try {
      if (isUserClient) {
        const { accountOpeningStatus } = await submitDraft();

        if (isFromWorkflow) {
          // Mark the auto accounts workflow as completed
          await completeWorkflow({ clientId });
        }

        // Refresh the accounts' data if the user can be routed to Personal Finances
        if (canAccessPersonalFinances) {
          await dispatch(fetchNetWorthData(clientId));
        }

        // Status will no longer be DRAFT -> Show the Account Opening Complete step
        mutateAccountStatus(accountOpeningStatus);
      } else {
        await sendEmailToClient({ managedAccountId: draftId, clientId });

        if (isFromWorkflow) {
          // Mark the auto accounts workflow as completed
          await completeWorkflow({ clientId });
        }

        // Refresh the accounts' data if the user can be routed to Personal Finances
        if (canAccessPersonalFinances) {
          await dispatch(fetchNetWorthData(clientId));
        }

        ADV.util.Router.go2('ApexAccount', { clientId, accountId: draftId });
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <GliaGlobalStyle />

      <Box minWidth={864} mt="48">
        <ScrollHereOnce key={selectedStepIndex} options={false} />

        {(isSubmittingDraft ||
          isFetchingNetWorth ||
          isCompletingWorkflow ||
          isSendingEmailToClient) && <FullPageLoading />}

        {submitDraftError && (
          <Box maxWidth={FORM_WIDTH} mx="auto">
            <Alert
              message={submitDraftError.message}
              variant="error"
              mb="24"
              onRequestClose={resetSubmitDraftMutation}
            />
          </Box>
        )}

        {isCompleteWorkflowError && (
          <Box maxWidth={FORM_WIDTH} mx="auto">
            <Alert
              message={MESSAGES.GENERIC_ERROR}
              variant="error"
              mb="24"
              onRequestClose={resetCompleteWorkflowMutation}
            />
          </Box>
        )}

        {isSendingEmailToClientError && (
          <Box maxWidth={FORM_WIDTH} mx="auto">
            <Alert
              message={MESSAGES.GENERIC_ERROR}
              variant="error"
              mb="24"
              onRequestClose={resetSendingEmailToClient}
            />
          </Box>
        )}

        <Text as="h1" maxWidth={FORM_WIDTH} mx="auto" fontSize="h4" fontWeight={500} mb="8">
          Account Opening
        </Text>

        <Steps selectedIndex={selectedStepIndex} onSelect={index => setSelectedStepIndex(index)}>
          <StepList maxWidth={FORM_WIDTH} mx="auto">
            {steps.map((step, index) => (
              <Step key={step} disabled={selectedStepIndex < index}>
                {step}
              </Step>
            ))}
          </StepList>

          <StepPanel>
            <PersonalInfo
              fieldName="primaryApplicant"
              draftAccount={draftAccount}
              onSuccess={() => setSelectedStepIndex(selectedStepIndex + 1)}
              onPrevious={() => {
                mutateDraftAccount({
                  customerType: null,
                  accountIraType: null,
                });
              }}
            />
          </StepPanel>

          {customerType === CustomerType.Joint && (
            <StepPanel>
              <PersonalInfo
                title="Your Partner's information"
                fieldName="secondApplicant"
                draftAccount={draftAccount}
                onSuccess={() => setSelectedStepIndex(selectedStepIndex + 1)}
                onPrevious={() => setSelectedStepIndex(selectedStepIndex - 1)}
              />
            </StepPanel>
          )}

          <StepPanel>
            <Employment
              draftAccount={draftAccount}
              onSuccess={() => setSelectedStepIndex(selectedStepIndex + 1)}
              onPrevious={() => setSelectedStepIndex(selectedStepIndex - 1)}
            />
          </StepPanel>

          <StepPanel>
            <AccountInfo
              draftAccount={draftAccount}
              onSuccess={() => setSelectedStepIndex(selectedStepIndex + 1)}
              onPrevious={() => setSelectedStepIndex(selectedStepIndex - 1)}
            />
          </StepPanel>

          {customerType === CustomerType.Ira && (
            <StepPanel>
              <BeneficiaryInfo
                draftAccount={draftAccount}
                onSuccess={() => setSelectedStepIndex(selectedStepIndex + 1)}
                onPrevious={() => setSelectedStepIndex(selectedStepIndex - 1)}
              />
            </StepPanel>
          )}

          <StepPanel>
            <Review
              draftAccount={draftAccount}
              onSuccess={handleSubmitDraft}
              onPrevious={() => setSelectedStepIndex(selectedStepIndex - 1)}
            />
          </StepPanel>
        </Steps>
      </Box>
    </>
  );
};
